import React from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
// IconButton + useTheme used to come from @mui/material — replaced below
// with a plain styled `<button>` and useTheme from @mui/system. Header is on
// the home critical path, so removing the @mui/material dependency here is
// what actually keeps the chunk off the LCP race (Header eagerly imports,
// so anything it imports lands in the entry chunk too).
import { useTranslate } from 'hooks/useTranslate';
import { styled, useTheme } from '@mui/system';
import { useDispatch, useSelector } from 'react-redux';
import Menu from 'components/Menu';
import storage from 'helpers/storage';
import parseQS from 'helpers/parseQS';
import { setOpenDrawer } from 'actions/app';
import { User, Menu as MenuIcon } from 'lucide-react';
import Logo from 'assets/images/profitroll-logo-white.svg?react';
import JobAltoLogo from 'assets/icons/jobatlo-logo.svg?react';
import { isLocalizedDomain } from 'helpers/getLocale';
import { getLocalizedPath } from 'helpers/getLocale';

// MUI's IconButton reset + ripple, distilled to a plain styled button so we
// don't drag in @mui/material on the home critical path. Same focus/hover
// semantics, accepts an `aria-label` for screen readers.
const BaseIconButton = styled('button')(() => ({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 8,
  margin: 0,
  border: 'none',
  borderRadius: '50%',
  background: 'transparent',
  cursor: 'pointer',
  appearance: 'none',
  WebkitTapHighlightColor: 'transparent',
  color: 'inherit',
  transition: 'background-color 0.2s',
  '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
  '&:focus-visible': { outline: '2px solid currentColor', outlineOffset: 2 }
}));

const HeaderWrapper = styled('header')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  position: 'relative',
  paddingLeft: 100,
  paddingRight: 100,
  paddingTop: 20,
  paddingBottom: 20,
  zIndex: 10,
  width: '100%',
  maxWidth: '1440px',
  margin: '0 auto',
  boxSizing: 'border-box',
  overflow: 'hidden',
  [theme.breakpoints.down('lg')]: {
    paddingLeft: 50,
    paddingRight: 50
  },
  [theme.breakpoints.down('md')]: {
    padding: '20px 30px'
  },
  [theme.breakpoints.down('sm')]: {
    paddingLeft: 0, // Will be set by HeaderWrapperInner
    paddingRight: 0,
    paddingTop: 0,
    paddingBottom: 0
  }
}));

const IconButtonWrapper = styled(BaseIconButton)(({ theme, transparent }) => ({
  width: transparent ? 36 : 36,
  height: transparent ? 36 : 36,
  border: `2px solid ${theme.palette.primary.main} !important`,
  borderRadius: '50%',
  padding: 4,
  color: theme.palette.primary.main,
  [theme.breakpoints.down('sm')]: {
    width: transparent ? 32 : 32,
    height: transparent ? 32 : 32,
    padding: transparent ? 3 : 3
  }
}));

const BurgerWrapper = styled(BaseIconButton)(({ mobile }) => ({
  display: mobile === 'true' ? 'block' : 'none',
  marginLeft: mobile === 'true' ? 4 : 0,
  marginRight: mobile === 'true' ? -4 : 0 // Decrease right margin
}));

const LogoWrapper = styled(isLocalizedDomain() ? JobAltoLogo : Logo)(({ theme }) => ({
  width: 140,
  height: 50,
  display: 'block',
  margin: 0,
  [theme.breakpoints.down('md')]: {
    width: 100,
    height: 30,
    paddingTop: 5
  },
  [theme.breakpoints.down('sm')]: {
    width: 120,
    height: 36,
    paddingTop: 3
  }
}));

const MenuWrapper = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center'
}));

const LogoLink = styled(Link)(() => ({
  padding: 0,
  margin: 0,
  display: 'inline-block',
  textAlign: 'left'
}));

const HeaderWrapperBackground = styled('div')(({ theme, backgroundColor, transparent }) => ({
  backgroundColor: transparent ? 'transparent' : (backgroundColor || theme.palette.textPrimary.main),
  transition: 'background-color 0.3s ease',
  position: 'relative',
  zIndex: 10
}));

const HeaderWrapperInner = styled(HeaderWrapper)(({ theme, transparent }) => ({
  paddingTop: 10,
  paddingBottom: 10,
  [theme.breakpoints.down('sm')]: {
    paddingLeft: '15px !important', // Explicitly override parent padding
    paddingRight: '20px !important',
    paddingTop: '10px !important', // Decreased from 20px to 10px
    paddingBottom: '10px !important', // Decreased from 20px to 10px
    maxWidth: '100%', // Override theme.wrapper maxWidth
    margin: 0 // Override theme.wrapper margin
  },
  ...(transparent ? {
    '& a, & button, & svg': {
      color: theme.palette.textPrimary.main,
      fill: theme.palette.textPrimary.main
    }
  } : {})
}));

const LogoWrapperInner = styled(LogoWrapper)(({ theme, transparent }) => ({
  width: 120,
  height: 35,
  paddingLeft: 0,
  paddingRight: 0,
  marginTop: 0,
  marginBottom: 0,
  [theme.breakpoints.down('sm')]: {
    width: 120,
    height: 36,
    marginTop: 0,
    marginBottom: 0,
    paddingTop: 4
  },
  ...(transparent ? {
    '& path': {
      fill: theme.palette.textPrimary.main
    }
  } : {})
}));

const Header = ({ isHomePage, backgroundColor, transparent }) => {
  const t = useTranslate('Header');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useSelector((state) => state.app.isMobile);
  const theme = useTheme();

  const handleRedirectLogin = React.useCallback(() => {
    navigate(getLocalizedPath('/sign-in'));
  }, [navigate]);

  const handleOpenDrawer = React.useCallback(() => {
    dispatch(setOpenDrawer(true));
  }, [dispatch]);

  React.useEffect(() => {
    const { referral } = parseQS(location.search);
    if (referral) {
      storage.setItem('referral', referral);
    }
  }, [location.search]);

  const RenderMenu = React.useCallback(
    () => (
      <MenuWrapper>
        <Menu transparent={transparent} userIconOnClick={handleRedirectLogin} />
        {!isMobile && (
          <IconButtonWrapper 
            transparent={transparent}
            onClick={handleRedirectLogin} 
            aria-label={t('UserIcon')}
            sx={{ 
              color: theme.palette.primary.main,
              '& svg': {
                color: theme.palette.primary.main,
                stroke: theme.palette.primary.main
              }
            }}>
            <User 
              size={24} 
              strokeWidth={2} 
              color={theme.palette.primary.main}
              style={{ 
                stroke: theme.palette.primary.main,
                fill: 'none', 
                strokeLinecap: 'round', 
                strokeLinejoin: 'round',
                color: theme.palette.primary.main
              }} 
            />
          </IconButtonWrapper>
        )}

        <BurgerWrapper 
          mobile={`${isMobile}`} 
          onClick={handleOpenDrawer} 
          aria-label={t('OpenMenu')}
          sx={{ color: 'primary.main' }}>
          <MenuIcon 
            size={isMobile ? 28 : 32} 
            strokeWidth={2}
            color={theme.palette.primary.main}
            style={{ 
              stroke: theme.palette.primary.main,
              fill: 'none',
              strokeLinecap: 'round',
              strokeLinejoin: 'round'
            }} 
          />
        </BurgerWrapper>
      </MenuWrapper>
    ),
    [handleRedirectLogin, t, isMobile, handleOpenDrawer, transparent, theme]
  );

  if (!isHomePage) {
    return (
      <HeaderWrapperBackground backgroundColor={backgroundColor} transparent={transparent}>
        <HeaderWrapperInner
          transparent={transparent}
          onClick={() => {
            import('services/analytics').then((Analytics) => {
              Analytics.default.event('LogoClick');
            });
          }}>
          <LogoLink to={getLocalizedPath('/')} aria-label={t('Logo')}>
            <LogoWrapperInner transparent={transparent} />
          </LogoLink>
          <RenderMenu />
        </HeaderWrapperInner>
      </HeaderWrapperBackground>
    );
  }

  return (
    <HeaderWrapper>
      <LogoLink to={getLocalizedPath('/')} aria-label={t('Logo')}>
        <LogoWrapper mobile={`${isMobile}`} />
      </LogoLink>
      <RenderMenu />
    </HeaderWrapper>
  );
};

export default Header;
